import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  Alert,
  ActivityIndicator,
  Keyboard,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { NavigationProp, RouteProp } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import twoFactorService from '../../services/twoFactorService';
import deviceTrustService from '../../services/deviceTrustService';
import OrnamentBackground from '../../components/OrnamentBackground';

import { createLogger } from '../../utils/logger';
const logger = createLogger('TwoFactorVerification');

interface TwoFactorVerificationScreenProps {
  navigation: NavigationProp<any>;
  route: RouteProp<{ params: { phoneNumber: string; isLogin?: boolean } }, 'params'>;
}

const TwoFactorVerificationScreen: React.FC<TwoFactorVerificationScreenProps> = ({
  navigation,
  route,
}) => {
  const { phoneNumber, isLogin = false } = route.params;
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);

  const inputRefs = useRef<Array<TextInput | null>>([]);

  useEffect(() => {
    // Start countdown timer
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  const handleCodeChange = (text: string, index: number) => {
    // Only allow numbers
    if (text && !/^\d$/.test(text)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = text;
    setCode(newCode);

    // Auto-focus next input
    if (text && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-verify when all digits are entered
    if (index === 5 && text) {
      const fullCode = [...newCode.slice(0, 5), text].join('');
      handleVerify(fullCode);
    }
  };

  const handleKeyPress = (e: any, index: number) => {
    if (e.nativeEvent.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async (verificationCode?: string) => {
    const codeToVerify = verificationCode || code.join('');

    if (codeToVerify.length !== 6) {
      Alert.alert('Invalid Code', 'Please enter all 6 digits');
      return;
    }

    setIsVerifying(true);
    Keyboard.dismiss();

    try {
      const result = await twoFactorService.verifyCode(phoneNumber, codeToVerify);

      if (result.success) {
        // Trust device if checkbox is checked
        if (trustDevice) {
          await deviceTrustService.trustCurrentDevice();
        }

        if (isLogin) {
          // Login flow - navigate to dashboard
          navigation.reset({
            index: 0,
            routes: [{ name: 'Dashboard' }],
          });
        } else {
          // Signup flow - mark 2FA complete and go to Splash (which will check onboarding)
          await AsyncStorage.setItem('@2fa_setup_complete', 'true');
          navigation.replace('Splash');
        }
      } else {
        // Invalid code
        Alert.alert('Verification Failed', result.message || 'Invalid code. Please try again.');
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      logger.error('Verification error:', error);
      Alert.alert('Error', 'Failed to verify code. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);

    try {
      const result = await twoFactorService.resendCode(phoneNumber);

      if (result.success) {
        Alert.alert('Code Sent', 'A new verification code has been sent to your phone.');
        setCountdown(60);
        setCanResend(false);
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      } else {
        Alert.alert('Error', result.message || 'Failed to resend code. Please try again.');
      }
    } catch (error) {
      logger.error('Resend error:', error);
      Alert.alert('Error', 'Failed to resend code. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const maskedPhone = twoFactorService.formatPhoneForDisplay(phoneNumber);

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />

      <View style={styles.content}>
        <View style={styles.header}>
          <Text style={styles.title}>Verification Code</Text>
          <Text style={styles.subtitle}>
            We sent a 6-digit code to {maskedPhone}
          </Text>
        </View>

        <View style={styles.codeContainer}>
          {code.map((digit, index) => (
            <TextInput
              key={index}
              ref={(ref) => (inputRefs.current[index] = ref)}
              style={[
                styles.codeInput,
                digit && styles.codeInputFilled,
              ]}
              value={digit}
              onChangeText={(text) => handleCodeChange(text, index)}
              onKeyPress={(e) => handleKeyPress(e, index)}
              keyboardType="number-pad"
              maxLength={1}
              selectTextOnFocus
              autoFocus={index === 0}
            />
          ))}
        </View>

        {isLogin && (
          <TouchableOpacity
            style={styles.trustDeviceContainer}
            onPress={() => setTrustDevice(!trustDevice)}
          >
            <View style={[styles.checkbox, trustDevice && styles.checkboxChecked]}>
              {trustDevice && <Text style={styles.checkmark}>✓</Text>}
            </View>
            <Text style={styles.trustDeviceText}>
              Trust this device for 30 days
            </Text>
          </TouchableOpacity>
        )}

        <TouchableOpacity
          style={[styles.verifyButton, isVerifying && styles.verifyButtonDisabled]}
          onPress={() => handleVerify()}
          disabled={isVerifying || code.join('').length !== 6}
        >
          {isVerifying ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <Text style={styles.verifyButtonText}>Verify</Text>
          )}
        </TouchableOpacity>

        <View style={styles.resendContainer}>
          {canResend ? (
            <TouchableOpacity
              onPress={handleResendCode}
              disabled={isResending}
            >
              <Text style={styles.resendText}>
                {isResending ? 'Sending...' : 'Resend Code'}
              </Text>
            </TouchableOpacity>
          ) : (
            <Text style={styles.countdownText}>
              Resend code in {countdown}s
            </Text>
          )}
        </View>

        <TouchableOpacity
          style={styles.changeNumberButton}
          onPress={() => navigation.goBack()}
        >
          <Text style={styles.changeNumberText}>Change Phone Number</Text>
        </TouchableOpacity>

        {/* Development bypass - remove in production */}
        {__DEV__ && !isLogin && (
          <TouchableOpacity
            style={styles.devSkipButton}
            onPress={async () => {
              await AsyncStorage.setItem('@2fa_setup_complete', 'true');
              navigation.replace('Splash');
            }}
          >
            <Text style={styles.devSkipText}>🛠️ Skip 2FA (Dev Only)</Text>
          </TouchableOpacity>
        )}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  content: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    marginTop: ms(60),
    paddingHorizontal: ms(20),
    paddingTop: ms(40),
  },
  header: {
    alignItems: 'center',
    marginBottom: ms(40),
  },
  title: {
    fontSize: ms(28, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginBottom: ms(10),
  },
  subtitle: {
    fontSize: ms(16, 0.3),
    color: '#666',
    textAlign: 'center',
    lineHeight: ms(24, 0.3),
  },
  codeContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: ms(30),
    paddingHorizontal: ms(10),
  },
  codeInput: {
    width: s(50),
    height: s(60),
    borderWidth: ms(2),
    borderColor: '#d8dbdd',
    borderRadius: ms(12),
    textAlign: 'center',
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#333',
    backgroundColor: '#FFFFFF',
  },
  codeInputFilled: {
    borderColor: '#29BFC0',
    backgroundColor: '#E8F5F5',
  },
  trustDeviceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(30),
    paddingHorizontal: ms(10),
  },
  checkbox: {
    width: s(24),
    height: s(24),
    borderRadius: ms(6),
    borderWidth: ms(2),
    borderColor: '#29BFC0',
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: ms(10),
  },
  checkboxChecked: {
    backgroundColor: '#29BFC0',
  },
  checkmark: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
  },
  trustDeviceText: {
    fontSize: ms(15, 0.3),
    color: '#333',
  },
  verifyButton: {
    backgroundColor: '#29BFC0',
    paddingVertical: ms(16),
    borderRadius: ms(25),
    alignItems: 'center',
    marginBottom: ms(20),
  },
  verifyButtonDisabled: {
    opacity: 0.6,
  },
  verifyButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  resendContainer: {
    alignItems: 'center',
    marginBottom: ms(15),
  },
  resendText: {
    color: '#29BFC0',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  countdownText: {
    color: '#666',
    fontSize: ms(16, 0.3),
  },
  changeNumberButton: {
    alignItems: 'center',
    paddingVertical: ms(10),
  },
  changeNumberText: {
    color: '#666',
    fontSize: ms(15, 0.3),
  },
  devSkipButton: {
    alignItems: 'center',
    paddingVertical: ms(12),
    marginTop: ms(20),
    backgroundColor: '#FF9F64',
    borderRadius: ms(25),
  },
  devSkipText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
});

export default TwoFactorVerificationScreen;
